import { createTheme, responsiveFontSizes, Theme } from '@mui/material/styles';
import { esES } from '@mui/material/locale';
import { esES as pickersEsES } from '@mui/x-date-pickers/locales';

/**
 * Tema de marca Kabandu para la web pública.
 *
 * La paleta es la MISMA del POS de escritorio (`renderer/lib/theme.ts`) y de las dos apps
 * (`app_theme.dart`): negro elegante sobre fondo claro, superficies blancas, tipografía
 * Inter. Antes la web usaba el indigo/rosa por defecto de MUI, que no es de la marca y no
 * se parecía a ninguna otra superficie del sistema.
 *
 * El modo oscuro toma los tonos de las apps (fondo #151515, superficie #1A1A1A), para que
 * un cliente que abre su ticket de noche vea lo mismo que en la app.
 */

const BRAND = {
    black: '#1A1A1A',
    blackLight: '#333333',
    blackDark: '#121212'
};

const light = {
    primary: { main: BRAND.black, light: BRAND.blackLight, dark: BRAND.blackDark, contrastText: '#FFFFFF' },
    secondary: { main: BRAND.blackLight, contrastText: '#FFFFFF' },
    background: { default: '#F7F7F9', paper: '#FFFFFF' },
    text: { primary: '#121212', secondary: '#595959' },
    divider: '#E6E6EB'
};

const dark = {
    primary: { main: '#FFFFFF', light: '#FFFFFF', dark: '#E0E0E0', contrastText: BRAND.blackDark },
    secondary: { main: '#E0E0E0', contrastText: BRAND.blackDark },
    background: { default: '#151515', paper: '#1A1A1A' },
    text: { primary: '#FFFFFF', secondary: '#A0A0A0' },
    divider: '#2C2C2C'
};

export const buildTheme = (mode: 'light' | 'dark'): Theme => {
    const palette = mode === 'light' ? light : dark;

    // El tema va PRIMERO y los idiomas después: `createTheme` arma la paleta (colores de acción,
    // hover, deshabilitado) solo con el primer argumento y a los demás solo los mezcla encima. Con
    // el idioma primero, el modo oscuro heredaba los colores de acción del claro (botones de
    // alternar casi invisibles).
    let theme = createTheme({
        palette: {
            mode,
            ...palette,
            error: { main: '#D32F2F' },
            warning: { main: '#B26A00' },
            info: { main: '#183CAD' },
            success: { main: '#2E7D32' }
        },
        shape: { borderRadius: 12 },
        typography: {
            fontFamily: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
            h1: { fontSize: '2.5rem', fontWeight: 700, letterSpacing: '-0.02em' },
            h2: { fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.02em' },
            h3: { fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.01em' },
            h4: { fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.01em' },
            h5: { fontSize: '1.25rem', fontWeight: 600 },
            h6: { fontSize: '1.05rem', fontWeight: 600 },
            subtitle2: { fontWeight: 600 },
            body1: { fontSize: '1rem' },
            body2: { fontSize: '0.9rem' },
            button: { textTransform: 'none' as const, fontWeight: 600 },
            // Etiqueta de sección: mayúsculas espaciadas, el gesto tipográfico de la marca.
            overline: { fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.12em', lineHeight: 2 }
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: palette.background.default,
                        WebkitFontSmoothing: 'antialiased'
                    }
                }
            },
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: { borderRadius: 12, padding: '10px 18px' },
                    sizeLarge: { padding: '14px 22px', fontSize: '1rem' }
                }
            },
            MuiPaper: {
                defaultProps: { elevation: 0 },
                styleOverrides: {
                    // Superficies delineadas en vez de sombreadas: apilar cards con sombra
                    // era justo lo que hacía ver "card sobre card".
                    root: { backgroundImage: 'none' }
                }
            },
            MuiOutlinedInput: {
                styleOverrides: { root: { borderRadius: 12, backgroundColor: palette.background.paper } }
            },
            MuiChip: { styleOverrides: { root: { fontWeight: 600 } } },
            MuiLinearProgress: { styleOverrides: { root: { borderRadius: 999, height: 8 } } },
            MuiDivider: { styleOverrides: { root: { borderColor: palette.divider } } },
            MuiAppBar: {
                defaultProps: { elevation: 0, color: 'inherit' },
                styleOverrides: {
                    root: {
                        backgroundColor: palette.background.paper,
                        borderBottom: `1px solid ${palette.divider}`,
                        backgroundImage: 'none'
                    }
                }
            }
        }
    }, esES, pickersEsES);

    theme = responsiveFontSizes(theme);
    return theme;
};
